import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const COOKIE_CONSENT_KEY = "feenex_cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentStatus;
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
      role="dialog"
      aria-label="Aviso de cookies"
      aria-modal="false"
    >
      <div className="bg-white/97 backdrop-blur-md border-t border-gray-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">

          {/* ── MOBILE layout ── */}
          <div className="flex flex-col gap-3 sm:hidden">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-amber-50 border border-amber-200">
                <i className="ri-shield-check-line text-amber-600 text-base"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 mb-0.5">
                  Privacidade e Cookies — LGPD
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Usamos cookies para melhorar sua experiência, em conformidade com a{" "}
                  <strong className="text-gray-800">Lei nº 13.709/2018</strong>.{" "}
                  <Link
                    to="/politica-de-privacidade"
                    className="text-amber-700 underline underline-offset-2 cursor-pointer"
                  >
                    Saiba mais
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleReject}
                className="whitespace-nowrap flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
              >
                Recusar
              </button>
              <button
                onClick={handleAccept}
                className="whitespace-nowrap flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700 rounded-md transition-colors cursor-pointer"
              >
                Aceitar todos
              </button>
            </div>
          </div>

          {/* ── DESKTOP layout ── */}
          <div className="hidden sm:flex flex-col gap-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-amber-50 border border-amber-200 mt-0.5">
                <i className="ri-shield-check-line text-amber-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Privacidade e Cookies — LGPD
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Utilizamos cookies essenciais para o funcionamento do site e, com seu consentimento,
                  cookies analíticos para melhorar sua experiência. Seus dados são tratados em
                  conformidade com a{" "}
                  <strong className="text-gray-800">Lei Geral de Proteção de Dados (Lei nº 13.709/2018)</strong>.
                  {" "}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-amber-700 underline underline-offset-2 hover:text-amber-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {showDetails ? "Ocultar detalhes" : "Saiba mais"}
                  </button>
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-3 ml-4">
                <button
                  onClick={handleReject}
                  className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
                >
                  Recusar
                </button>
                <button
                  onClick={handleAccept}
                  className="whitespace-nowrap px-5 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700 rounded-md transition-colors cursor-pointer"
                >
                  Aceitar todos
                </button>
              </div>
            </div>

            {showDetails && (
              <div className="ml-14 grid grid-cols-3 gap-4 pt-2 border-t border-gray-100">
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-line text-green-600 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800 mb-0.5">Cookies Essenciais</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Necessários para o funcionamento básico do site. Sempre ativos e não requerem consentimento.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bar-chart-line text-amber-600 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800 mb-0.5">Cookies Analíticos</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Nos ajudam a entender como você usa o site para melhorar continuamente a experiência.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-gray-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-800 mb-0.5">Seus Direitos</p>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Você pode revogar seu consentimento a qualquer momento. Consulte nossa{" "}
                      <Link
                        to="/politica-de-privacidade"
                        className="text-amber-700 underline underline-offset-2 hover:text-amber-900 cursor-pointer"
                      >
                        Política de Privacidade
                      </Link>
                      .
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
